import React, { useEffect, useState } from 'react';
import { Row, Col, Breadcrumb } from 'antd';
import PageHeader from '@iso/components/utility/pageHeader';
import Fraudmeter from '@iso/components/Fraudmeter/Fraudmeter';
import GoogleMap from '../../components/GoogleMap/GoogleMap';
import TransactionInfo from '../../components/TransactionInfo/TransactionInfo';
import { Pill } from '../../components/Pill/Pill.styles';
import { useNavigate, useParams } from 'react-router-dom';
import { StatusPill } from '../../components/BreadcumbPill/StatusPill.styles';
import { getTransactionsById } from '../../services/transactions/api';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FeedbackModal } from '../../components/Modal/Modal.styles';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Markdown } from '../../components/Markdown/index.style';
import { useIntl } from 'react-intl';
import remarkGfm from 'remark-gfm';
import pinSVG from '../../assets/images/pin.svg';
import { TransactionInfoContainer } from '@/components/TransactionInfo/TransactionInfo.styles';

const titleStyle = { fontSize: 20, fontWeight: 400, marginBottom: 20 };

const validateGeolocation = (geolocation) => {
  if (!geolocation) return null;

  const latitude = parseFloat(geolocation.latitude);
  const longitude = parseFloat(geolocation.longitude);
  const accuracy = parseFloat(geolocation.accuracy);

  const isValid =
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude) &&
    !Number.isNaN(accuracy) &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Number.isFinite(accuracy);

  return isValid ? { latitude, longitude, accuracy } : null;
};

export default function TransactionsDetail() {
  const { id } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState();
  const [loading, setLoading] = useState(true);

  const [scoreInfoModal, setScoreInfoModal] = useState(false);
  const [riskInfoModal, setRiskInfoModal] = useState(false);
  const [locationInfoModal, setLocationInfoModal] = useState(false);
  const [captureInfoModal, setCaptureInfoModal] = useState(false);

  useEffect(() => {
    getTransactionsById(id)
      .then(transaction => {
        setTransaction(transaction.data);
        setLoading(false);
      })
      .catch(() => {
        navigate('/transactions', {
          state: { message: 'Transaction not found' },
        });
      });
  }, [id]);

  const renderCaptureStatus = status => {
    if (status === 'suspicious') {
      return <IntlMessages id="page.transaction_detail.risk.suspicious" />;
    } else if (status === 'healthy') {
      return <IntlMessages id="page.transaction_detail.risk.healthy" />;
    }
    return <IntlMessages id="page.transaction_detail.risk.unknown" />;
  };

  const renderCaptureStatusColor = status => {
    if (status === 'suspicious') {
      return '#E16408';
    } else if (status === 'healthy') {
      return '#00ABAB';
    }
    return '#263238';
  };

  const renderWebhookTime = time => {
    if (time) {
      return time.toFixed(3) + 's';
    }

    return '-';
  };

  return loading ? (
    <div>Loading</div>
  ) : (
    <>
      <PageHeader>
        <div className="flex items-center justify-start">
          <Breadcrumb
            separator={
              <div className="!justify-center !items-center">
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L3.58579 5L0.292893 8.29289C-0.0976315 8.68342 -0.0976315 9.31658 0.292893 9.70711C0.683417 10.0976 1.31658 10.0976 1.70711 9.70711L5.70711 5.70711C6.09763 5.31658 6.09763 4.68342 5.70711 4.29289L1.70711 0.292893C1.31658 -0.097631 0.683418 -0.0976311 0.292893 0.292893Z"
                    fill="#7D7D7D"
                  />
                </svg>
              </div>
            }
            items={[
              {
                title: 'Transactions',
                href: '/transactions',
                className: '!font-normal !pointer-events-none !text-lg',
              },
              {
                title: 'Transaction details',
                className: '!text-primary !font-semibold !text-lg',
              },
            ]}
          />
        </div>
      </PageHeader>
      <div className="flex flex-wrap justify-start items-center mb-2 gap-y-2">
        <span className="text-2xl font-semibold mr-2">Type</span>
        <div className="flex flex-wrap w-full sm:w-auto">
          <StatusPill
            className={transaction.type === 'verification' ? `low` : ''}
          >
            Verification
          </StatusPill>
          <StatusPill className={transaction.type === 'onboarding' ? `low` : ''}>
            Onboarding
          </StatusPill>
		  <StatusPill className={transaction.type === 'id_capture' ? `low` : ''}>
            ID Capture
          </StatusPill>
        </div>
      </div>
      <Row
        type="flex"
        gutter={[32, 32]}
      >
        <Col
          span={18}
          xs={24}
          md={24}
          lg={18}
        >
          <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm h-full">
            <div className="flex mx-auto py-6 px-8 gap-2">
              <h3 style={titleStyle}>Scores</h3>
              <div className="mt-1 cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setScoreInfoModal(true)}
                  style={{ fontSize: '20px' }}
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-evenly gap-4">
              <Fraudmeter
                title={'FACE MATCH'}
                subtitle={transaction.scores?.face_match?.match_with}
                percentage={transaction.scores?.face_match?.score * 160 - 160}
                value={transaction.scores?.face_match?.score}
                threshold={transaction.scores?.face_match?.threshold}
              />
              <Fraudmeter
                title={'LIVENESS'}
                percentage={transaction.scores?.liveness?.score * 160 - 160}
                value={transaction.scores?.liveness?.score}
                threshold={transaction.scores?.liveness?.threshold}
              />
            </div>
          </div>
        </Col>
        <Col
          span={6}
          xs={24}
          md={24}
          lg={6}
        >
          <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm h-full ">
            <div className="flex mx-auto py-6 px-8 gap-2">
              <h3 style={titleStyle}>
                <IntlMessages id="page.overview.transactions.filters.risk" />
              </h3>
              <div className="mt-1 cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setRiskInfoModal(true)}
                  style={{ fontSize: '20px' }}
                />
              </div>
            </div>
            <div className="grid place-items-center grid-cols-1 gap-4 ">
              <Pill
                className={`${transaction.risk_level === 'high' ? 'high' : ''}`}
              >
                HIGH
              </Pill>
              <Pill
                className={`${transaction.risk_level === 'low' ? 'low' : ''}`}
              >
                LOW
              </Pill>
              <Pill
                className={`${transaction.risk_level === 'unknown' ? 'unknown' : ''}`}
              >
                UNKNOWN
              </Pill>
            </div>
          </div>
        </Col>
        <Col
          span={12}
          xs={24}
          md={24}
          lg={12}
        >
          <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm h-full">
            <div className="flex mx-auto gap-2">
              <h3 style={titleStyle}>
                <IntlMessages id="page.transaction_detail.geolocation" />
              </h3>
              <div className="mt-1 cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setLocationInfoModal(true)}
                  style={{ fontSize: '20px' }}
                />
              </div>
            </div>
            {(() => {
              const validGeolocation = validateGeolocation(
                transaction.capture?.information?.geolocation
              );

              return validGeolocation ? (
                <GoogleMap
                  lat={validGeolocation.latitude}
                  lng={validGeolocation.longitude}
                  radius={validGeolocation.accuracy}
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-center h-full min-h-[300px]">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2 2l20 20"
                      />
                    </svg>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    <IntlMessages id="page.transaction_detail.geolocation.unavailable" />
                  </h3>
                  <p className="text-gray-500 max-w-xs">
                    <IntlMessages id="page.transaction_detail.geolocation.unavailable.description" />
                  </p>
                </div>
              );
            })()}
          </div>
        </Col>
        <Col
          span={12}
          xs={24}
          md={24}
          lg={12}
        >
          <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm h-full">
            <div className="flex mx-auto gap-2">
              <h3 style={titleStyle}>
                <IntlMessages id="page.transaction_detail.capture-information" />
                <span
                  className="ml-1"
                  style={{
                    color: renderCaptureStatusColor(
                      transaction.capture?.status ?? 'unknown',
                    ),
                  }}
                >
                  <b>
                    {renderCaptureStatus(
                      transaction.capture?.status ?? 'unknown',
                    )}
                  </b>
                </span>
              </h3>
              <div className="mt-1 cursor-pointer">
                <InfoCircleOutlined
                  onClick={() => setCaptureInfoModal(true)}
                  style={{ fontSize: '20px' }}
                />
              </div>
            </div>
            <TransactionInfo info={transaction} />
          </div>
        </Col>
        <Col
          span={12}
          xs={24}
          md={24}
          lg={12}
        >
          <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm h-full">
            <div className="flex mx-auto gap-2">
              <h3 style={titleStyle}>
                <IntlMessages id="page.transaction_detail.webhook_modal.title" />
              </h3>
            </div>
            <Row
              type="flex"
              gutter={[32, 32]}
            >
              <Col
                span={12}
                xs={24}
                md={24}
                lg={12}
              >
                <TransactionInfoContainer>
                  <div className="flex gap-2">
                    <p>
                      <IntlMessages id="page.transaction_detail.webhook_modal.response" />
                    </p>
                    <p className="font-bold">
                      {transaction.webhook_info?.response_code ?? '-'}
                    </p>
                  </div>
                </TransactionInfoContainer>
              </Col>
              <Col
                span={12}
                xs={24}
                md={24}
                lg={12}
              >
                <TransactionInfoContainer>
                  <div className="flex gap-2">
                    <p>
                      <IntlMessages id="page.transaction_detail.webhook_modal.duration" />
                    </p>
                    <p className="font-bold">
                      {renderWebhookTime(
                        transaction.webhook_info?.request_time,
                      )}
                    </p>
                  </div>
                </TransactionInfoContainer>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      <FeedbackModal
        title={<IntlMessages id="page.transaction_detail.score_modal.title" />}
        open={scoreInfoModal}
        onCancel={() => setScoreInfoModal(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: 'page.transaction_detail.score_modal.p',
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={<IntlMessages id="page.transaction_detail.risk_modal.title" />}
        open={riskInfoModal}
        onCancel={() => setRiskInfoModal(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: 'page.transaction_detail.risk_modal.p',
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={
          <IntlMessages id="page.transaction_detail.location_modal.title" />
        }
        open={locationInfoModal}
        onCancel={() => setLocationInfoModal(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: 'page.transaction_detail.location_modal.p',
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
      <FeedbackModal
        title={
          <IntlMessages id="page.transaction_detail.capture_modal.title" />
        }
        open={captureInfoModal}
        onCancel={() => setCaptureInfoModal(false)}
        footer={null}
      >
        <Markdown
          children={intl.formatMessage({
            id: 'page.transaction_detail.capture_modal.p',
          })}
          remarkPlugins={[remarkGfm]}
        />
      </FeedbackModal>
    </>
  );
}
